import React from 'react';
import Modal from './Modal';
import { GiftIcon, CalendarDaysIcon, ClockIcon, SparklesIcon, ExclamationTriangleIcon, StarIcon } from '@heroicons/react/24/outline';

const TEMPLATES = [
    {
        id: '1-year-anniversary',
        name: '1 Year Anniversary',
        description: 'Send a special discount, offer a free gift, or just say Happy Anniversary.',
        icon: GiftIcon,
        data: {
            trigger: 'condition_met',
            action: 'email',
            offset: 0,
            condition: { andor: 'and', conditions: [] },
            target_conditions: {
                andor: 'and',
                conditions: [
                    {
                        left: 'client-days-since-created',
                        operator: '>=',
                        right: '365'
                    }
                ]
            },
            settings: {
                to: '{client-email}',
                subject: 'Happy 1 Year Anniversary, {client-first_name}!',
                body: 'Hi {client-first_name},<br />\n<br />It\n\'s been a whole year since you first joined us! We wanted to take a moment to thank you for being such a valued client.<br />\n<br />\nTo celebrate your anniversary, we\'d love to offer you a special discount on your next appointment.<br />\n<br />\nThank you for your continued trust in us!<br />\n<br />\nBest regards,<br />\nThe Team'
            },
            flowable_type: 'App\\Models\\Client',
        }
    },
    {
        id: '10-sessions',
        name: '10 Sessions',
        description: 'Celebrate when clients have reached 10 appointments scheduled total.',
        icon: CalendarDaysIcon,
        data: {
            trigger: 'condition_met',
            action: 'email',
            offset: 0,
            condition: { andor: 'and', conditions: [] },
            target_conditions: {
                andor: 'and',
                conditions: [
                    {
                        left: 'client-total-appointments',
                        operator: '>=',
                        right: '10'
                    }
                ]
            },
            settings: {
                to: '{client-email}',
                subject: 'Congratulations on your 10th session, {client-first_name}!',
                body: 'Hi {client-first_name},<br />\n<br />\nCongratulations! You\'ve just reached a major milestone - your 10th appointment with us!<br />\n<br />\nWe\'re so proud of your commitment and dedication. It\'s been wonderful watching your progress.<br />\n<br />\nHere\'s to many more sessions together!<br />\n<br />\nCelebrating with you,\nThe Team'
            },
            flowable_type: 'App\\Models\\Client',
        }
    },
    {
        id: 'rebooking-reminder',
        name: 'Rebooking Reminder',
        description: 'Remind clients to rebook when their last appointment was >30 days ago.',
        icon: ClockIcon,
        data: {
            trigger: 'condition_met',
            action: 'email',
            offset: 0,
            condition: { andor: 'and', conditions: [] },
            target_conditions: {
                andor: 'and',
                conditions: [
                    {
                        left: 'daysSinceLastAppointment',
                        operator: '>',
                        right: '30'
                    }
                ]
            },
            settings: {
                to: '{client-email}',
                subject: 'We miss you, {client-first_name}!',
                body: 'Hi {client-first_name},\n\nIt\'s been a while since your last visit with us, and we wanted to reach out to see how you\'re doing!\n\nWe\'d love to have you back and continue supporting you on your journey. Would you like to schedule your next appointment?\n\nWe\'re here whenever you\'re ready!\n\nWarm regards,\nThe Team'
            },
            flowable_type: 'App\\Models\\Client',
        }
    },
    {
        id: 'happy-new-year',
        name: 'Happy New Year',
        description: 'Send New Year wishes when current month is January.',
        icon: SparklesIcon,
        data: {
            trigger: 'condition_met',
            action: 'email',
            offset: 0,
            condition: { andor: 'and', conditions: [] },
            target_conditions: {
                andor: 'and',
                conditions: [
                    {
                        left: 'current-month',
                        operator: '=',
                        right: '1'
                    }
                ]
            },
            settings: {
                to: '{client-email}',
                subject: 'Happy New Year, {client-first_name}!',
                body: 'Hi {client-first_name},\n\nHappy New Year! We hope this year brings you joy, health, and all the wonderful things you deserve.\n\nThank you for being part of our community. We\'re excited to continue this journey with you in the new year!\n\nHere\'s to new beginnings and achieving your goals together.\n\nWishing you all the best,\nThe Team'
            },
            flowable_type: 'App\\Models\\Client',
        }
    },
    {
        id: 'ask-for-review',
        name: 'Ask for Review',
        description: 'Ask clients to leave a review the day after their last appointment.',
        icon: StarIcon,
        data: {
            trigger: 'condition_met',
            action: 'email',
            offset: 0,
            condition: { andor: 'and', conditions: [] },
            target_conditions: {
                andor: 'and',
                conditions: [
                    {
                        left: 'daysSinceLastAppointment',
                        operator: '>=',
                        right: '1'
                    }
                ]
            },
            settings: {
                to: '{client-email}',
                subject: 'Can we get a review, {client-first_name}?',
                body: 'Hi {client-first_name},<br />\n<br />\nWe hope you had a great experience with us. We\'d love to hear your thoughts and feedback. Please take a moment to leave a review on Google:<br />\n<a href="https://maps.google.com/">https://maps.google.com/</a><br />\n<br />\nYour feedback helps us grow and improve. Thank you!'
            },
            flowable_type: 'App\\Models\\Client',
        }
    },
    {
        id: 'empty-class-alert',
        name: 'Empty Class Alert',
        description: 'Email admins 2 days before a class starts when it has 0 attendees.',
        icon: ExclamationTriangleIcon,
        data: {
            trigger: 'condition_met',
            action: 'email',
            offset: 0,
            condition: { andor: 'and', conditions: [] },
            target_conditions: {
                andor: 'and',
                conditions: [
                    {
                        left: 'class-attendees',
                        operator: '=',
                        right: '0'
                    },
                    {
                        left: 'class-days-until-start',
                        operator: '=',
                        right: '2'
                    }
                ]
            },
            settings: {
                subject: 'Alert: Empty Class Starting Soon - {event-type-name}',
                message: 'Hi Team,\n\nThis is an automated alert to let you know that the following class is starting in 2 days and currently has 0 attendees:\n\n**Class Details:**\n- Class: {event-type-name}\n- Start Time: {class-start}\n- Instructor: {class-booked_with}\n- Current Attendees: {class-attendees}/{class-max_attendees}\n\nYou may want to consider:\n- Promoting the class to fill remaining spots\n- Reaching out to previous attendees\n- Canceling the class if needed',
                send_to_admins: true
            },
            flowable_type: 'App\\Models\\ClassInstance',
        }
    }
];

const TemplateCard = ({ template, onSelect }) => {
    const IconComponent = template.icon;

    return (
        <div
            className="border border-white bg-white rounded-lg p-4 cursor-pointer hover:border-primary-700 transition-colors"
            onClick={() => onSelect(template)}
        >
            <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                    <IconComponent className="w-6 h-6 text-primary-700" />
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-medium text-gray-900 mb-1">
                        {template.name}
                    </h3>
                    <p className="text-sm text-gray-500">
                        {template.description}
                    </p>
                </div>
            </div>
        </div>
    );
};

const MarketingAutomationTemplateModal = ({ isOpen, onClose, onSelectTemplate }) => {
    const handleSelectTemplate = (template) => {
        onSelectTemplate(template.data);
        onClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Choose a Template"
            size="lg"
        >
            <h2 className="mb-6">Select Automation</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {TEMPLATES.map((template) => (
                    <TemplateCard
                        key={template.id}
                        template={template}
                        onSelect={handleSelectTemplate}
                    />
                ))}
            </div>
        </Modal>
    );
};

export default MarketingAutomationTemplateModal;
